import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircleIcon, ClipboardDocumentIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../../utils/format';

// How to put the chat widget on a website, per platform.
//
// This used to be one instruction -- "paste before the closing </body> tag"
// -- four clicks deep in an agent's Share menu. Most owners run WordPress,
// Shopify or Wix and have never seen a </body> tag, so each platform gets the
// clicks it actually needs, and anyone can forward the lot to a developer.

export const widgetScriptUrl = () => {
  // Defaults to the dashboard origin, so dev emits a dev URL and prod a prod
  // URL. REACT_APP_WIDGET_HOST moves every snippet to one permanent host.
  const host = process.env.REACT_APP_WIDGET_HOST || window.location.origin;
  return `${host.replace(/\/$/, '')}/embed/serviti-chat-widget.js`;
};

export const embedSnippet = (agentId) => `<!-- Serviti AI Chat Widget -->
<script
  src="${widgetScriptUrl()}"
  data-agent-id="${agentId}"
></script>`;

export const PLATFORMS = [
  {
    key: 'html',
    label: 'Any website',
    steps: [
      'Open the HTML of your site template, the part shared by every page (often called the footer or layout).',
      'Paste the code just before the closing </body> tag.',
      'Save and publish. The chat button appears at the bottom right of every page.',
    ],
  },
  {
    key: 'wordpress',
    label: 'WordPress',
    steps: [
      'In WordPress, go to Plugins → Add New, search for "WPCode", then install and activate it.',
      'Go to Code Snippets → Header & Footer and paste the code into the Footer box.',
      'Click Save Changes. The chat button appears on every page.',
    ],
  },
  {
    key: 'shopify',
    label: 'Shopify',
    steps: [
      'In Shopify admin, go to Online Store → Themes, click ⋯ next to your live theme, then Edit code.',
      'Open Layout → theme.liquid and paste the code just above the closing </body> tag.',
      'Click Save. The chat button appears on every page of your store.',
    ],
  },
  {
    key: 'wix',
    label: 'Wix',
    steps: [
      'In your Wix dashboard, go to Settings → Custom code and click + Add Custom Code.',
      'Paste the code, choose All pages and Load code once, and place it in Body - end.',
      'Click Apply. Custom code needs a Wix Premium plan with a connected domain.',
    ],
  },
  {
    key: 'squarespace',
    label: 'Squarespace',
    steps: [
      'Open Code Injection: Settings → Advanced → Code Injection (or Pages → Custom code → Code injection).',
      'Paste the code into the Footer box.',
      'Click Save. Code injection needs a Squarespace plan that includes it.',
    ],
  },
  {
    key: 'gtm',
    label: 'Google Tag Manager',
    steps: [
      'In Tag Manager, go to Tags → New → Tag Configuration → Custom HTML and paste the code.',
      'Under Triggering, choose All Pages, then save the tag.',
      'Click Submit, then Publish. The chat button appears once the new version is live.',
    ],
  },
];

const developerEmail = (agentName, platform, snippet) => {
  const subject = `Please add our Serviti chat widget to the website`;
  const body = [
    'Hi,',
    '',
    `Please add the ${agentName || 'Serviti'} chat widget to our website. It is one script tag that must load on every page.`,
    '',
    `Steps (${platform.label}):`,
    ...platform.steps.map((step, i) => `${i + 1}. ${step}`),
    '',
    'The code:',
    '',
    snippet,
    '',
    'Thank you!',
  ].join('\n');
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/** Where this agent's widget has been seen, or a waiting state. */
export const InstallStatus = ({ hosts }) => {
  if (hosts && hosts.length > 0) {
    const [latest] = hosts;
    return (
      <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-100">
        <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-300" aria-hidden="true" />
        <div>
          <p className="font-semibold">Widget seen on {latest.host}</p>
          <p className="text-xs text-green-800 dark:text-green-200">
            Last seen {formatDate(latest.last_seen)}
            {hosts.length > 1 && ` · also on ${hosts.slice(1).map((h) => h.host).join(', ')}`}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
      Not seen on a website yet. Once the code is on your site, open any page and this changes to show
      your website within a minute.
    </div>
  );
};

const InstallWidgetGuide = ({ agent, hosts = null }) => {
  const [platformKey, setPlatformKey] = useState('html');
  const platform = PLATFORMS.find((p) => p.key === platformKey) || PLATFORMS[0];
  const snippet = embedSnippet(agent.id);
  const agentName = (agent.branding_allowed && agent.brand_name) || agent.name;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success('Code copied');
    } catch {
      toast.error('Could not copy. Select the code and copy it instead.');
    }
  };

  return (
    <div className="space-y-4">
      {hosts !== null && <InstallStatus hosts={hosts} />}

      <div>
        <p className="text-sm font-semibold text-gray-900 dark:text-white">Where is your website built?</p>
        <div role="tablist" aria-label="Website platform" className="mt-2 flex flex-wrap gap-2">
          {PLATFORMS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              id={`install-tab-${p.key}`}
              aria-selected={p.key === platformKey}
              aria-controls="install-steps"
              onClick={() => setPlatformKey(p.key)}
              className={`min-h-[44px] rounded-lg border px-3 py-2 text-sm font-semibold ${
                p.key === platformKey
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div id="install-steps" role="tabpanel" aria-labelledby={`install-tab-${platform.key}`}>
        <ol className="list-decimal space-y-1.5 pl-5 text-sm text-gray-700 dark:text-gray-200">
          {platform.steps.map((step) => <li key={step}>{step}</li>)}
        </ol>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Your code</span>
          <button
            type="button"
            onClick={copy}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-gray-700"
          >
            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
            Copy code
          </button>
        </div>
        <pre className="mt-1 overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100"><code>{snippet}</code></pre>
      </div>

      <a
        href={developerEmail(agentName, platform, snippet)}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
      >
        <EnvelopeIcon className="h-4 w-4" aria-hidden="true" />
        Email these instructions to my developer
      </a>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Greeting, branding and WhatsApp handoff load from this agent&rsquo;s settings in Serviti, so you never
        need to change the code after it is installed.
      </p>
    </div>
  );
};

export default InstallWidgetGuide;
